import makeDebug from "debug";
import type { User } from "oidc-client-ts";
import posthog, { type PostHogConfig } from "posthog-js";
import type { ReportType } from "types/Report";
import { getEnvironment } from "./environment";

export const POSTHOG_API_KEY =
	"phc_hiXf7cFyINiiKZsJIcjKlNTAZZYO8C1DlfnXRAT4hiQ";
export const POSTHOG_OPTIONS: Partial<PostHogConfig> = {
	api_host: "https://ph.kultify.com",
	ui_host: "https://eu.i.posthog.com",
	opt_out_capturing_by_default: true,
	opt_out_persistence_by_default: true,
	opt_in_site_apps: true,
};

const debug = makeDebug("tracker");

class Tracker {
	public track(eventName: string, props?: Record<string, unknown>): void {
		if ((window as any).Cypress || import.meta.env.DEV) {
			return;
		}
		debug("track event", eventName, props);
		setTimeout(() => {
			try {
				posthog.capture(eventName, {
					...props,
					product: "kultify-portal",
					portal_version: import.meta.env.VITE_VERSION ?? "development",
					environment: getEnvironment(),
				});
			} catch (err) {
				console.warn("tracking error", err);
			}
		}, 0);
	}

	public identify(user: User) {
		if ((window as any).Cypress) {
			return;
		}
		debug("Identify user", user.profile?.sub, {
			email: user.profile?.email,
			name: user.profile.name,
		});
		setTimeout(() => {
			try {
				posthog.identify(user.profile.sub, {
					email: user.profile?.email,
					name: user.profile?.name,
					roles: user.profile?.roles,
				});
				if (import.meta.env.VITE_VERSION) {
					posthog.people.set("app_version", import.meta.env.VITE_VERSION);
				}
			} catch (err) {
				console.warn("tracking error", err);
			}
		}, 0);
	}

	public trackShowOrganizationSelect(): void {
		this.track("show-organization-select");
	}

	public trackShowDashboard(organizationId: string): void {
		this.track("show-dashboard", { organizationId });
	}

	public trackShowReportList(organizationId: string): void {
		this.track("show-report-list", { organizationId });
	}

	// REPORT
	public trackShowReport(
		organizationId: string,
		reportId: string,
		type: string,
	): void {
		this.track("show-report", { organizationId, reportId, type });
	}

	public trackShowReportElementDetails(
		organizationId: string,
		reportId: string,
	) {
		this.track("report-show-element-details", { organizationId, reportId });
	}

	public trackReportChangeSegments(
		organizationId: string,
		reportId: string,
		count: number,
		type: "detail" | "benchmark",
	) {
		this.track("report-change-segments", {
			organizationId,
			reportId,
			count,
			type,
		});
	}

	public trackSaveDetailsAsImage(type: string) {
		this.track("report:save-as-image", { type });
	}

	public trackShareReport(
		organizationId: string,
		reportId: string,
		type: ReportType,
		limitToSelectedData: boolean,
	) {
		this.track("report:share", {
			organizationId,
			reportId,
			type,
			limitToSelectedData,
		});
	}

	// REPORT / HEATMAP
	public trackSaveHeatmapAsImage(organizationId: string, reportId: string) {
		this.track("heatmap:save-as-image", {
			organizationId,
			reportId,
		});
	}

	public trackShowSelectBenchmarkReportData(
		organizationId: string,
		reportId: string,
	) {
		this.track("benchmark:show-select-data", { organizationId, reportId });
	}

	public trackSelectBenchmarkReportData(
		organizationId: string,
		reportId: string,
		count: number,
	) {
		this.track("benchmark:select-data", {
			organizationId,
			reportId,
			count,
		});
	}

	// SHARED REPORT ADMIN
	public trackShowManageSharedReports(organizationId: string) {
		this.track("shared-report-admin:show", { organizationId });
	}

	public trackDeleteSharedReport(organizationId: string) {
		this.track("shared-report-admin:delete", { organizationId });
	}

	// SHARED REPORT
	public trackShowSharedReport(organizationId: string, reportId: string) {
		this.track("shared-report-show", { organizationId, reportId });
	}

	public trackShowSharedReportElementDetails(
		organizationId: string,
		reportId: string,
	) {
		this.track("shared-report-show-element-details", {
			organizationId,
			reportId,
		});
	}

	public trackSharedReportChangeDataSegment(
		organizationId: string,
		reportId: string,
	) {
		this.track("shared-report-change-data-segment", {
			organizationId,
			reportId,
		});
	}

	public trackSharedReportChangeReferenceSegments(
		organizationId: string,
		reportId: string,
	) {
		this.track("shared-report-change-reference-segments", {
			organizationId,
			reportId,
		});
	}

	public trackShowAttributes(organizationId: string) {
		this.track("show-attributes", { organizationId });
	}
	// TODO extend attribute tracking

	public trackShowMemberships(organizationId: string) {
		this.track("show-memberships", { organizationId });
	}
	public trackShowMembershipDetails(
		organizationId: string,
		membershipId: string,
	) {
		this.track("show-membership-details", { organizationId, membershipId });
	}

	/*  COMMENTS */
	public trackShowComments(organizationId: string, query: any) {
		this.track("comments:show", { organizationId, query });
	}

	public trackSearchComments(
		organizationId: string,
		query: string,
		ai: boolean,
	) {
		this.track("comments:search", { organizationId, query, ai });
	}

	public trackSelectCommentAttachmentFilter(
		organizationId: string,
		attachmentId: string,
	) {
		this.track("comments:select-attachment", {
			organizationId,
			attachmentId,
		});
	}

	public trackRemoveCommentAttachmentFilter(
		organizationId: string,
		attachmentId: string,
	) {
		this.track("comments:remove-attachment", {
			organizationId,
			attachmentId,
		});
	}

	public trackExportComments(organizationId: string, type: string) {
		this.track("comments:export", { organizationId, type });
	}

	/* FOLLOW UPS */
	public trackOpenFollowUpConversation(
		organizationId: string,
		followUpId?: string,
	) {
		this.track("followups:open-conversation", {
			organizationId,
			followUpId,
		});
	}
	public trackSendFollowUpMessage(organizationId: string, followUpId?: string) {
		this.track("followups:send-message", {
			organizationId,
			followUpId,
		});
	}
	public trackShowFollowUps(organizationId: string) {
		this.track("followups:show-page", { organizationId });
	}
	public trackHandleArchiveFollowUp(
		organizationId: string,
		followUpId?: string,
	) {
		this.track("followups:archive", {
			organizationId,
			followUpId,
		});
	}
	public trackHandleRestoreFollowUp(
		organizationId: string,
		followUpId?: string,
	) {
		this.track("followups:restore", {
			organizationId,
			followUpId,
		});
	}

	/* LABELS */
	public trackShowLabels(organizationId: string) {
		this.track("labels:show", {
			organizationId,
		});
	}
	public trackCreateLabelStep(organizationId: string, step: string) {
		this.track("labels:create", {
			organizationId,
			step,
		});
	}
	public trackUpdateLabelStep(organizationId: string, step: string) {
		this.track("labels:update", {
			organizationId,
			step,
		});
	}
	public trackDeleteLabel(organizationId: string) {
		this.track("labels:delete", {
			organizationId,
		});
	}

	/* SHARED CONTENT */
	public trackShareContentWithMember(
		organizationId: string,
		membershipId: string,
	) {
		this.track("sharedContent:share-content", {
			organizationId,
			membershipId,
		});
	}
	public trackShowSharedContentPage(organizationId: string) {
		this.track("sharedContent:show-shared-content-page", {
			organizationId,
		});
	}
	public trackDownloadSharedContent(organizationId: string) {
		this.track("sharedContent:download-shared-content", { organizationId });
	}

	/* PEER FEEDBACK */
	public trackShowPeerFeedbackTab(organizationId: string, tab: string) {
		this.track("peerFeedback:open-tab", { organizationId, tab });
	}

	public trackPeerFeedbackCreateFlow(
		organizationId: string,
		step: string,
		details: any,
	) {
		this.track("peerFeedback:create-flow", {
			organizationId,
			step,
			details,
		});
	}

	public trackShowCreateManagedPeerFeedbackDialog(organizationId: string) {
		this.track("peerFeedback:show-create-managed-dialog", { organizationId });
	}

	public trackCreateManagedPeerFeedback(organizationId: string, details: any) {
		this.track("peerFeedback:create-managed", { organizationId, details });
	}

	public trackShowPeerFeedbackDetails(
		organizationId: string,
		peerFeedbackState: string,
	) {
		this.track("peerFeedback:show-details", {
			organizationId,
			peerFeedbackState,
		});
	}

	public trackPeerFeedbackChangeFeedbackGroups(
		organizationId: string,
		numberOfFeedbackGroups: number,
	) {
		this.track("peerFeedback:changed-feedback-groups", {
			organizationId,
			numberOfFeedbackGroups,
		});
	}

	public trackPeerFeedbackEdited(organizationId: string, details: any) {
		this.track("peerFeedback:edited", { organizationId, details });
	}

	public trackPeerFeedbackStartRequested(organizationId: string) {
		this.track("peerFeedback:start-requested", { organizationId });
	}

	public trackClosePeerFeedback(organizationId: string) {
		this.track("peerFeedback:close", { organizationId });
	}

	public trackDeletePeerFeedback(organizationId: string) {
		this.track("peerFeedback:delete", { organizationId });
	}

	// OKRS
	public trackShowOKRList(
		organizationId: string,
		mode: string,
		filter: any,
		sort: any,
	) {
		this.track("okrs:show", { organizationId, mode, filter, sort });
	}

	public trackShowOKRDetails(organizationId: string, okrId: string) {
		this.track("okrs:show-details", { organizationId, okrId });
	}

	public trackChangeOKRStatus(
		organizationId: string,
		okrId: string,
		oldStatus: string,
		newStatus: string,
	) {
		this.track("okrs:change-status", {
			organizationId,
			okrId,
			oldStatus,
			newStatus,
		});
	}

	public trackEditOKR(organizationId: string, okrId: string) {
		this.track("okrs:edit", { organizationId, okrId });
	}

	public trackCreateOKR(
		organizationId: string,
		assignedToSelf: boolean,
		withTemplate: boolean,
	) {
		this.track("okrs:create", {
			organizationId,
			assignedToSelf,
			withTemplate,
		});
	}

	public trackDeleteOKR(organizationId: string, okrId: string) {
		this.track("okrs:delete", { organizationId, okrId });
	}

	public trackChangeOKRAssignedTo(
		organizationId: string,
		okrId: string,
		assignedToSelf: boolean,
	) {
		this.track("okrs:change-assigned-to", {
			organizationId,
			okrId,
			assignedToSelf,
		});
	}

	// OKR / Key Results
	public addOKRKeyResult(organizationId: string, okrId: string) {
		this.track("okrs:add-key-result", { organizationId, okrId });
	}

	public trackDeleteOKRKeyResult(
		organizationId: string,
		okrId: string,
		keyResultId: string,
	) {
		this.track("okrs:delete-key-result", {
			organizationId,
			okrId,
			keyResultId,
		});
	}

	public trackEditOKRKeyResult(
		organizationId: string,
		okrId: string,
		keyResultId: string,
	) {
		this.track("okrs:edit-key-result", {
			organizationId,
			okrId,
			keyResultId,
		});
	}

	public trackChangeOKRKeyResultStatus(
		organizationId: string,
		okrId: string,
		_keyResultId: string,
		oldStatus: boolean,
		newStatus: boolean,
	) {
		this.track("okrs:change-key-result-status", {
			organizationId,
			okrId,
			oldStatus,
			newStatus,
		});
	}

	// OKR / TAGS
	public trackAddOKRTag(organizationId: string) {
		this.track("okr-tag:create", { organizationId });
	}

	public trackDeleteOKRTag(organizationId: string) {
		this.track("okr-tag:delete", { organizationId });
	}

	public trackEditOKRTag(organizationId: string) {
		this.track("okr-tag:edit", { organizationId });
	}

	// OKR / COMMENTS
	public trackAddOKRComment(organizationId: string, okrId: string) {
		this.track("okr-comment:add", { organizationId, okrId });
	}

	public trackDeleteOKRComment(organizationId: string, id: string) {
		this.track("okr-comment:delete", { organizationId, id });
	}

	public trackEditOKRComment(organizationId: string, id: string) {
		this.track("okr-comment:edit", { organizationId, id });
	}

	// PPTX
	public trackDownloadPPTX(
		organizationId: string,
		reportId: string,
		type: string,
		exportType: string,
		focusSegmentPath: string,
	) {
		this.track("pptx:download", {
			organizationId,
			reportId,
			type,
			exportType,
			focusSegmentPath,
		});
	}

	public trackDownloadManyPPTX(
		organizationId: string,
		reportIds: string[],
		type: string,
	) {
		this.track("pptx:download", { organizationId, reportIds, type });
	}

	// ADDITIONAL CONTENT
	public trackShowAdditionalContent(
		organizationId: string,
		contentId: string,
		source: string,
	) {
		this.track("additional-content:show", {
			organizationId,
			contentId,
			source,
		});
	}

	public trackAddOKRFromAdditionalContent(
		organizationId: string,
		contentId: string,
	) {
		this.track("additional-content:add-okr", { organizationId, contentId });
	}
}

const tracker = new Tracker();

export default tracker;

export function useTracker() {
	return tracker;
}
